import {
  createTranslatedMessageComponent,
  createUseTranslateMessage,
  useSafeIntl,
} from "@bloomberg/media.i18n";

import React from "react";
import translationLabels from "i18n/label_ids.json";

export const useTranslateMessage = createUseTranslateMessage(translationLabels);

export type TranslateMessageFn = ReturnType<typeof useTranslateMessage>;

export const TranslatedMessage =
  createTranslatedMessageComponent(translationLabels);

export const useIntl = useSafeIntl;

export type TranslatedMessageProps = React.ComponentProps<
  typeof TranslatedMessage
>;

export type TranslatedMessageType = {
  id: string;
  defaultMessage: string;
};

export type LocalizedTextProps = {
  /**
   * The text or translation object to render.
   * - If a string, it’s rendered directly.
   * - If a TranslatedMessageType object, it renders a <TranslatedMessage /> component.
   */
  value?: string | TranslatedMessageType;
};

/**
 * LocalizedText Component
 *
 * A lightweight wrapper around <TranslatedMessage /> that accepts either:
 * - A plain string (renders directly), or
 * - A translation descriptor ({ id, defaultMessage }) to localize via TranslatedMessage.
 *
 * The motivation for this wrapper is **backward compatibility** —
 * it allows existing hardcoded strings (e.g. `title: 'Futures'`)
 * to coexist with newer translation objects
 * (e.g. `title: { id: "futures", defaultMessage: "Futures" }`)
 *
 * Returns null if no value is provided.
 */
export const LocalizedText = ({ value }: LocalizedTextProps) => {
  if (!value) return null;

  return typeof value === "string" ? (
    <>{value}</>
  ) : (
    <TranslatedMessage
      id={value.id as any}
      defaultMessage={value.defaultMessage}
    />
  );
};
